// deps
import React, { ReactElement, useReducer, useMemo, useState, useEffect } from "react";

// internal-deps
import { Some } from "@swiggy-private/js-fp";
import { GlobalStore, LocalStorage } from "@swiggy-private/core-ui";
import Analytics, { DEFAULT_ANALYTICS_CONTEXT_VALUE } from "@swiggy-private/analytics";
import {
    UserContext,
    SwgyHeadersKeys,
    CheckoutContext,
    ApiContext,
    ApiContextValue,
    reducer as checkoutReducer,
    CheckoutState,
    ConfigContext,
    ConfigContextValue,
    HardwareBackContext,
    useUserInfo,
    AnalyticsContext,
    useCheckoutWebDynamicConfig,
    CheckoutWebDynamicConfigContext,
    PublicApiProvider,
    BackNavigationContextProvider,
    CHECKOUT_WEB_WP_CONFIG_KEYS,
    CHECKOUT_WEB_WP,
    ENABLE_WEBVIEW_REFRESH_DO_STORAGE_KEY,
    CheckoutWebWPConfig,
    recordNREvent,
    NEWRELIC_TABLES,
} from "@swiggy-private/checkout-core";
import { ConfirmOrderContext } from "@swiggy-private/checkout-core";
import { getAnalyticsConfigParams } from "@swiggy-private/checkout-core";
import { OrderApiData, ApiResponse } from "@swiggy-private/payment-types";
import { safeJsonParse } from "@swiggy-private/js-utils";

// Providers
import { CartActionProvider } from "@dineout-cart-client/components/cart-action-provider";
import { DineoutCartContextProvider } from "@dineout-cart-client/components/dineout-cart-context-provider/DineoutCartContextProvider";
import { DineoutSharedCartProvider } from "../dineout-dinecash-provider/DineCashPollingProvider";

export interface ContextProviderProps {
    config: {
        apiHost: string;
        paymentHost: string;
        analyticsHost: string;
        cachePaymentData?: boolean;
        paymentReturnUrl?: string;
        addMoneyReturnUrl?: string;
        simplPostLinkingRedirectUrl: string;
    };
    headers: { [key in Partial<SwgyHeadersKeys>]: string };
    initialState: CheckoutState;
    /** SSR theme name from the HTML shell; client routes read the stamped html attr when unset. */
    checkoutThemeName?: string | null;
    children: ReactElement;
}

/** Server-resolved checkout theme name for SSR-safe theme resolution in nested routes. */
export const CheckoutThemeNameContext = React.createContext<string | null | undefined>(undefined);

const updateApiContextValue = (apiHost: string, paymentHost: string): ApiContextValue => ({
    host: Some(apiHost),
    paymentHost: Some(paymentHost),
});
const updateConfigContextValue = (config: ContextProviderProps["config"]): ConfigContextValue => ({
    host: Some(config.apiHost),
    analyticsHost: Some(config.analyticsHost),
    paymentReturnUrl: Some(config.paymentReturnUrl),
    addMoneyReturnUrl: Some(config.addMoneyReturnUrl),
    addSwiggyMoneyReturnUrl: Some(""),
    cachePaymentData: Some(config.cachePaymentData),
    simplPostLinkingRedirectUrl: Some(config.simplPostLinkingRedirectUrl),
    enableConfirmOrderDifferentialOrderIdAlert: Some(false),
});

export function ContextProvider(props: ContextProviderProps): ReactElement {
    const { config, initialState, checkoutThemeName, children } = props;
    const [user, setUser] = useUserInfo();
    const checkoutWebDynamicConfigValue = useCheckoutWebDynamicConfig();

    const [checkoutState, setCheckoutState] = useReducer(checkoutReducer, initialState);
    const [hardwareBackState, setHardwareBackState] = useState({ shouldHandle: false });
    const apiContextValue = useMemo(
        () => updateApiContextValue(config.apiHost, config.paymentHost),
        [config.apiHost, config.paymentHost],
    );
    const configContextValue = useMemo(() => updateConfigContextValue(config), [config]);
    const checkoutContextValue = useMemo(
        () => ({
            checkoutState,
            setCheckoutState,
        }),
        [checkoutState],
    );
    const analyticsContextValue = useMemo(() => {
        const analyticsConfig = getAnalyticsConfigParams(user, config.analyticsHost);
        const analytics = (analyticsConfig && new Analytics(analyticsConfig)) || DEFAULT_ANALYTICS_CONTEXT_VALUE;
        return analytics;
    }, [user, config.analyticsHost]);

    const hardwareBackContextValue = useMemo(() => {
        return {
            state: hardwareBackState,
            setState: setHardwareBackState,
        };
    }, [hardwareBackState]);

    const userContextValue = useMemo(() => {
        return {
            user,
            setUser,
        };
    }, [user, setUser]);

    //This is required to make confirmOrderContext available to Dineout payment success.
    const [confirmOrderContextState, setConfirmOrderContextState] = useState<ApiResponse<OrderApiData>>();

    const confirmOrderContextValue = useMemo(() => {
        return {
            confirmOrderContext: confirmOrderContextState,
            setConfirmOrderContext: setConfirmOrderContextState,
        };
    }, [confirmOrderContextState]);

    useEffect(() => {
        try {
            const cwWpConfigStr = checkoutWebDynamicConfigValue.checkoutWebWP[CHECKOUT_WEB_WP.CW_WP_CONFIG];
            const checkoutWebWpConfig = safeJsonParse<CheckoutWebWPConfig>(cwWpConfigStr);

            const enableDoWvRefresh = checkoutWebWpConfig
                ? Boolean(checkoutWebWpConfig[CHECKOUT_WEB_WP_CONFIG_KEYS.ENABLE_DO_WV_REFRESH])
                : false;
            const enableDoWvRefreshFromStorage = LocalStorage.getItem(ENABLE_WEBVIEW_REFRESH_DO_STORAGE_KEY, false);

            // set the value in storage if it is different from the value in the config
            if (enableDoWvRefreshFromStorage !== enableDoWvRefresh) {
                LocalStorage.setItem(ENABLE_WEBVIEW_REFRESH_DO_STORAGE_KEY, enableDoWvRefresh);
            }
        } catch (err) {
            recordNREvent({
                eventType: NEWRELIC_TABLES.DO_WV_REFRESH_CONFIG_ERROR,
                error: JSON.stringify(err),
            });
        }

    }, [checkoutWebDynamicConfigValue.checkoutWebWP]);

    return (
        <AnalyticsContext.Provider value={analyticsContextValue}>
            <CheckoutWebDynamicConfigContext.Provider value={checkoutWebDynamicConfigValue}>
                <CheckoutThemeNameContext.Provider value={checkoutThemeName}>
                    <CheckoutContext.Provider value={checkoutContextValue}>
                        <UserContext.Provider value={userContextValue}>
                            <ConfigContext.Provider value={configContextValue}>
                                <PublicApiProvider>
                                    <HardwareBackContext.Provider value={hardwareBackContextValue}>
                                        <BackNavigationContextProvider>
                                            <ApiContext.Provider value={apiContextValue}>
                                                <DineoutCartContextProvider>
                                                    <CartActionProvider>
                                                        <ConfirmOrderContext.Provider
                                                            value={confirmOrderContextValue}
                                                        >
                                                            <DineoutSharedCartProvider>
                                                                <GlobalStore>{children}</GlobalStore>
                                                            </DineoutSharedCartProvider>
                                                        </ConfirmOrderContext.Provider>
                                                    </CartActionProvider>
                                                </DineoutCartContextProvider>
                                            </ApiContext.Provider>
                                        </BackNavigationContextProvider>
                                    </HardwareBackContext.Provider>
                                </PublicApiProvider>
                            </ConfigContext.Provider>
                        </UserContext.Provider>
                    </CheckoutContext.Provider>
                </CheckoutThemeNameContext.Provider>
            </CheckoutWebDynamicConfigContext.Provider>
        </AnalyticsContext.Provider>
    );
}
